import { useContext, useState } from "react";
import { Alert, Stack } from "@mui/material";
import { Add } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Link, useParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import moment from "moment";
import Layout from "../Frames/Layout";
import Loader from "../Tools/Loader";
import Top from "./Common/Top";
import SessionCard from "./Sessions/SessionCard";
import Learners from "./Sessions/Learners";
import useChapters from "./Hooks/useChapters";
import useSessions from "./Hooks/useSessions";
import useCourseEnrollments from "./Hooks/useCourseEnrollments";
import API from "../../services/API";
import ToastContext from "../../services/Context/ToastContext";
import { txt } from "../../services/translation";

export default function Sessions(){

    const {id} = useParams()

    const {setToast} = useContext(ToastContext)

    const queryClient = useQueryClient()

    const chapters = useChapters()
    const sessions = useSessions()
    const enrollments = useCourseEnrollments()

    /** Only the session being edited is unfolded, the others stay one line tall */
    const [open, setOpen] = useState(null)

    const [creating, setCreating] = useState(false)

    const sessionsKey = ['design', id, 'sessions']
    const enrollmentsKey = ['design', id, 'enrollments']

    /**
     * Applies the change to the cache at once and sends it afterwards,
     * putting the previous state back if the server refuses it.
     */
    const optimistic = (changes, request) => {

        const previous = changes.map(([key]) => [key, queryClient.getQueryData(key)])

        changes.forEach(([key, update]) => queryClient.setQueryData(key, update))

        request().catch(() => {
            previous.forEach(([key, data]) => queryClient.setQueryData(key, data))
            setToast({severity: 'error', message: txt('errorOccurred')})
        })
    }

    /** Needs the id the server gives, so it waits for it rather than guessing */
    const createSession = () => {
        setCreating(true)
        API.post(`/design/sessions`, {
            courseId: id,
            title: 'Nouvelle session',
            start: moment().format('YYYY-MM-DD'),
            program: {}
        })
        .then(response => {
            queryClient.setQueryData(sessionsKey, list => [...(list || []), response.data])
            setOpen(response.data.id)
        })
        .catch(() => setToast({severity: 'error', message: txt('errorOccurred')}))
        .finally(() => setCreating(false))
    }

    const saveSession = data => {
        setOpen(null)
        optimistic(
            [[sessionsKey, list => list.map(s => s.id === data.id ? data : s)]],
            () => API.put(`/design/sessions/${data.id}`, data)
        )
    }

    const deleteSession = session => {
        if(!window.confirm(`Supprimer la session « ${session.title} » ?`)) return
        setOpen(null)
        optimistic(
            [
                [sessionsKey, list => list.filter(s => s.id !== session.id)],
                [enrollmentsKey, list => list && list.map(e => e.sessionId === session.id ? {...e, sessionId: null} : e)]
            ],
            () => API.delete(`/design/sessions/${session.id}`)
        )
    }

    const attach = (enrollmentId, sessionId) => optimistic(
        [[enrollmentsKey, list => list.map(e => e.id === enrollmentId ? {...e, sessionId: sessionId || null} : e)]],
        () => API.put(`/design/enrollments/${enrollmentId}/session`, {sessionId: sessionId || null})
    )

    if( id === 'new' ) return <Layout menu='design' tab='sessions'>
        <Alert severity="warning">
            {txt('pleaseFinalize')} <Link to={`/design/${id}`}><u>{txt('creationLinkText')}</u></Link> {txt('editPlanMessage')}
        </Alert>
    </Layout>

    return <Layout menu='design' tab='sessions' style={{background: 'rgb(250,250,250)'}}>

        <Stack spacing={2} sx={{maxWidth: 1100, margin: 'auto'}}>

            <Top title="Sessions de formation" />

            {chapters.isLoading || sessions.isLoading ? <Loader /> : <>

                <Stack direction='row'>
                    <LoadingButton startIcon={<Add />} onClick={createSession} loading={creating} loadingPosition='start'>
                        Session
                    </LoadingButton>
                </Stack>

                {sessions.data.length === 0 &&
                    <Alert severity='info'>
                        Aucune session. Sans session, les apprenants voient toute
                        la formation dès leur inscription.
                    </Alert>
                }

                {sessions.data.map(s =>
                    <SessionCard
                        key={s.id}
                        session={s}
                        chapters={chapters.data}
                        expanded={open === s.id}
                        onToggle={() => setOpen(open === s.id ? null : s.id)}
                        onSave={saveSession}
                        onDelete={() => deleteSession(s)}
                    />
                )}

            </>}

            {/* Loaded on its own so a course with thousands of learners
                does not hold back the sessions above */}
            <Learners
                enrollments={enrollments.data}
                loading={enrollments.isLoading}
                sessions={sessions.data || []}
                onAttach={attach}
            />

        </Stack>

    </Layout>

}
